import type { DashboardSnapshot } from '@dash/shared';
import { Card } from '../components/Card.tsx';
import { Sparkline } from '../components/Sparkline.tsx';
import { BgpRow } from '../components/BgpRow.tsx';
import { SectionHeader } from '../components/SectionHeader.tsx';
import { fmtInt } from '../lib/format.ts';

interface RedeProps {
  snap: DashboardSnapshot;
}

export function RedeView({ snap }: RedeProps) {
  const onlinePct = snap.rede.clientesTotal
    ? (snap.rede.clientesOnline / snap.rede.clientesTotal) * 100
    : 0;

  const established = snap.rede.bgp.filter((p) => p.state === 'established').length;
  const total = snap.rede.bgp.length;
  const onlineTone = onlinePct >= 95 ? 'ok' : onlinePct >= 90 ? 'warn' : 'crit';
  const bgpTone = total === 0 ? 'neutral' : established === total ? 'ok' : 'crit';

  return (
    <div className="grid gap-4 p-6 lg:grid-cols-12">
      {/* HERO */}
      <div className="col-span-full">
        <div className={`card-base p-8 ${onlineTone === 'ok' ? 'card-glow-ok' : onlineTone === 'warn' ? 'card-glow-warn' : 'card-glow-crit'}`}>
          <span className={`tone-strip tone-strip-${onlineTone}`} aria-hidden />
          <div className="relative grid grid-cols-12 items-end gap-6">
            <div className="col-span-12 lg:col-span-7">
              <div className="eyebrow">clientes online · NOC view</div>
              <div className={`mt-3 font-bold leading-none tracking-tight tabular-nums text-[--color-${onlineTone}] text-[7rem] lg:text-[10rem]`}>
                {fmtInt(snap.rede.clientesOnline)}
              </div>
              <div className="mt-4 flex items-baseline gap-3 text-[--color-fg-dim]">
                <span className={`text-3xl font-semibold tabular-nums text-[--color-${onlineTone}]`}>{onlinePct.toFixed(1)}%</span>
                <span className="text-sm uppercase tracking-wider">ativos / {fmtInt(snap.rede.clientesTotal)} contratos</span>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-wider text-[--color-fg-muted]">
                <span>últimas 24h</span><span>step 5min</span>
              </div>
              <Sparkline values={snap.rede.sparkline24h} height={150} />
            </div>
          </div>
        </div>
      </div>

      {/* KPIs principais */}
      <SectionHeader label="indicadores" accent="info" />
      <div className="col-span-12 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card
          title="OLTs em alerta"
          tone={snap.rede.oltsAlerta > 0 ? 'crit' : 'ok'}
          value={fmtInt(snap.rede.oltsAlerta)}
          hint={`/ ${fmtInt(snap.rede.oltsTotal)}`}
          size="xl"
          footer={<span>monitor: <b className="text-[--color-fg]">Zabbix grupo OLTs</b></span>}
        />
        <Card
          title="Triggers críticos"
          tone={snap.rede.zabbixCriticos > 0 ? 'crit' : 'ok'}
          value={fmtInt(snap.rede.zabbixCriticos)}
          hint="severity 5 · Disaster"
          size="xl"
          footer={<span>{snap.rede.zabbixCriticos > 0 ? 'INTERVENÇÃO REQUERIDA' : 'rede estável'}</span>}
        />
        <Card
          title="BGP established"
          tone={bgpTone}
          value={`${established}/${total}`}
          hint="peers ativos"
          size="xl"
          footer={<span>via SNMP NE40E</span>}
        />
      </div>

      {/* BGP table */}
      <SectionHeader label="peers BGP" hint={`${total} configurados`} accent="accent" />
      <div className="col-span-12">
        <div className="card-base p-5">
          {snap.rede.bgp.length === 0 ? (
            <div className="py-12 text-center text-sm text-[--color-fg-muted]">aguardando dados SNMP...</div>
          ) : (
            <div className="grid gap-2 lg:grid-cols-2">
              {snap.rede.bgp.map((p) => (
                <BgpRow key={p.peer} peer={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
